import React from 'react';
import pt from 'prop-types';
import { connect } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { get, extend, reduce, pick, find } from 'lodash';

import api from 'services/api';

import actions from 'actions';

import {
  locationWithQuery,
  formatDate,
  isEmpty,
  CURRENCY_CODES,
  objectToArray,
  updateQueryObject,
} from 'utils';
import { filterQueryToWhere, buildSortValue } from 'utils/collection';
import { accountName } from 'utils/account';
import { canRunAppContentActions } from 'utils/app';

import View from 'components/view/view';
import CollectionPage from 'components/collection/collection';
import CollectionLoading from 'components/collection/loading';
import { withViewPreferences } from './WithViewPreferences';

// Standard lists also show the app function actions declared in the app list
// views of their collection: `actions` and `extra_actions` in the Actions
// menu, and `bulk_actions` in the bulk bar
const selectListActions = createSelector(
  [
    (views) => views,
    (_views, extraActions) => extraActions,
    (_views, _extraActions, bulkActions) => bulkActions,
  ],
  (views, extraActions = [], bulkActions = []) => {
    const appViews = (views || []).filter(
      (view) =>
        view.app_id &&
        view.active !== false &&
        view.type === 'list' &&
        view.id === 'list',
    );

    return {
      extraActions: [
        ...extraActions,
        ...appViews
          .flatMap((view) => [
            ...(view.actions || []),
            ...(view.extraActions || []),
          ])
          .filter((action) => action.function),
      ],
      bulkActions: [
        ...bulkActions,
        ...appViews.flatMap((view) => view.bulkActions || []),
      ],
    };
  },
);

const NO_BULK_ACTIONS = [];

const mapStateToProps = (state, props) => {
  const { model, view, childId, selectable } = props;
  const data = childId
    ? state.data.children[childId] || state.data.initialState()
    : state.data;

  // Content view lists (CollectionList) and child lists pass their own
  // actions, and app actions are left out for users who cannot run them
  const { extraActions, bulkActions = NO_BULK_ACTIONS } =
    view ||
    childId ||
    !canRunAppContentActions(
      state.user,
      state.account?.features?.advanced_user_permissions,
      model,
    )
      ? props
      : selectListActions(
          state.content?.viewsByCollection?.[model],
          props.extraActions,
          props.bulkActions,
        );

  return {
    client: state.client,
    user: state.user,
    collection: data.collection,
    collectionCount: data.collectionCount,
    selection: data.selection,
    extraActions,
    bulkActions,
    selectable: selectable !== undefined ? selectable : bulkActions.length > 0,
    content: state.content,
    categories: state.categories,
    lookup: state.lookup,
    loading: data.loading,
    loadingCollection: state.data.loadingCollection,
  };
};

function collectionCountQuery(query) {
  if (!query) {
    return query;
  }

  const { include, expand, ...countQuery } = query;
  return countQuery;
}

const mapDispatchToProps = (
  dispatch,
  { model, childId, query = {}, queryCurrency, afterFetch, endpoint },
) => ({
  fetchCollectionCount: (queryOverride) => {
    const baseQuery = collectionCountQuery(queryOverride || query);
    const countEndpoint = endpoint ? `${endpoint}/count` : undefined;

    return dispatch(
      childId
        ? actions.data.childFetchCollectionCount(
            childId,
            model,
            countEndpoint,
            baseQuery,
          )
        : actions.data.fetchCollectionCount(model, countEndpoint, baseQuery),
    );
  },

  fetchCollection: async (locationQuery, tabsIn, filtersIn, queryOverride) => {
    const baseQuery = { ...(queryOverride || query) };
    const where = { $and: [...get(baseQuery, 'where.$and', [])] };
    const tabs = objectToArray(tabsIn);
    const filters = objectToArray(filtersIn);

    if (tabs) {
      const locationTab = find(tabs, { id: locationQuery.tab });
      const defaultTab = find(tabs, { id: 'default' });
      if (locationTab) {
        if (locationTab.query) {
          where.$and.push(locationTab.query);
        }
      } else if (defaultTab) {
        if (defaultTab.query) {
          where.$and.push(defaultTab.query);
        }
      }
    }

    let extraQuery = {};
    if (filters) {
      const { filterWhere, filterQuery } = filterQueryToWhere(
        filters,
        locationQuery,
      );
      if (filterWhere.length > 0) {
        where.$and = where.$and.concat(filterWhere);
        extraQuery.$and = (extraQuery.$and || []).concat(filterWhere);
      }
      if (filterQuery.length > 0) {
        for (let exq of filterQuery) {
          extend(extraQuery, exq);
        }
      }
    }

    if (isEmpty(where.$and)) {
      delete where.$and;
    }

    if (queryCurrency) {
      if (CURRENCY_CODES.length) {
        baseQuery.$currency = CURRENCY_CODES;
      } else {
        delete baseQuery.$currency;
      }
    }

    const dataQuery = {
      ...baseQuery,
      ...extraQuery,
      page: locationQuery.page,
      search: locationQuery.search || baseQuery.search,
      limit: locationQuery.limit || baseQuery.limit,
      sort: locationQuery.sort || baseQuery.sort,
      where: {
        ...(locationQuery.where || {}),
        ...(baseQuery.where || {}),
        ...where,
      },
    };

    const fetchArgs = [model, dataQuery, locationQuery, extraQuery, endpoint];
    const result = await dispatch(
      childId
        ? actions.data.childFetchCollection(childId, ...fetchArgs)
        : actions.data.fetchCollection(...fetchArgs),
    );

    if (afterFetch) {
      await afterFetch(result);
    }

    return result;
  },

  selectAll: () =>
    dispatch(
      childId ? actions.data.childSelectAll(childId) : actions.data.selectAll(),
    ),
  selectOne: (id, isRange) =>
    dispatch(
      childId
        ? actions.data.childSelectOne(childId, id, isRange)
        : actions.data.selectOne(id, isRange),
    ),
  selectClear: () =>
    dispatch(
      childId
        ? actions.data.childSelectClear(childId)
        : actions.data.selectClear(),
    ),
  setLoading: (is) =>
    dispatch(
      childId
        ? actions.data.childLoading(childId, is)
        : actions.data.loading(is),
    ),

  loadCategories: () => {
    return dispatch(actions.categories.load());
  },
});

class Collection extends React.Component {
  static propTypes = {
    model: pt.string.isRequired,
    endpoint: pt.string,
    child: pt.bool,
    page: pt.func,
    view: pt.object,
    query: pt.object,
    location: pt.object,
    locationQuery: pt.object,
    router: pt.object,
    title: pt.string,
    headerTitle: pt.string,
    component: pt.object,
    emptyTitle: pt.any,
    emptyDescription: pt.any,
    emptyComponent: pt.func,
    collection: pt.object,
    collectionCount: pt.number,
    loadingCollection: pt.bool,
    categories: pt.object,
    extraActions: pt.array,
    bulkActions: pt.array,
    tabs: pt.oneOfType([pt.object, pt.array]),
    filters: pt.oneOfType([pt.object, pt.array]),

    onQuery: pt.func,
    selectAll: pt.func.isRequired,
    selectOne: pt.func.isRequired,
    selectClear: pt.func.isRequired,
    setLoading: pt.func.isRequired,
    searchHandler: pt.func,
    loadCategories: pt.func.isRequired,
    fetchCollection: pt.func.isRequired,
    fetchCollectionCount: pt.func.isRequired,
    banner: pt.node,
  };

  static contextTypes = {
    client: pt.object.isRequired,
    user: pt.object.isRequired,
    openModal: pt.func.isRequired,
    setLastUrl: pt.func.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      loaded: false,
      search: null,
      filters: objectToArray(props.filters),
      filterKeys: null,
      filterOp: null,
      filterValue: null,
      filterValues: null,
      filterListVisible: false,
      collectionEmpty: !props.collectionCount,
      onSubmitSearch: this.onSubmitSearch.bind(this),
      onChangeSearch: this.onChangeSearch.bind(this),
      onClickShowFilterList: this.onClickShowFilterList.bind(this),
      onClickApplyFilter: this.onClickApplyFilter.bind(this),
      onClickRemoveFilter: this.onClickRemoveFilter.bind(this),
      onClickSelectAll: this.onClickSelectAll.bind(this),
      onClickSelectClear: this.onClickSelectClear.bind(this),
      onClickSelectRow: this.onClickSelectRow.bind(this),
      onClickSort: this.onClickSort.bind(this),
      onClickPage: this.onClickPage.bind(this),
      onChangeLimit: this.onChangeLimit.bind(this),
      onClickBulkAction: this.onClickBulkAction.bind(this),
    };
  }

  async componentDidMount() {
    const { loadCategories, fetchCollectionCount } = this.props;

    this.preloadBanner();

    if (loadCategories) {
      // Load categories and storefront first to support location filtering
      await loadCategories();
      await Promise.all([
        fetchCollectionCount(this.props.query),
        this.fetchCollection(),
      ]);
    }

    this.context.setLastUrl(null);
  }

  componentDidUpdate(prevProps) {
    const { location, locationQuery, fetchCollectionCount } = this.props;

    const isCollectionChanged =
      location && location.pathname !== prevProps.location.pathname;

    const query = locationQuery || location.query;
    const prevQuery = prevProps.locationQuery || prevProps.location.query;
    const isQueryChanged = query !== prevQuery;

    if (isCollectionChanged) {
      this.setState({ loaded: false, filterListVisible: false });
      this.preloadBanner();
      Promise.all([
        fetchCollectionCount(this.props.query),
        this.fetchCollection(),
      ]);
    } else if (isQueryChanged) {
      this.getFiltersHydrated().then((filters) => {
        this.setState({ filterListVisible: false, filters });
        return Promise.all([
          fetchCollectionCount(this.props.query),
          this.fetchCollection(),
        ]);
      });
    }

    // Mark as loaded when collection is done initially loading
    if (
      !this.state.loaded &&
      prevProps.loadingCollection &&
      !this.props.loadingCollection &&
      this.props.collection
    ) {
      Promise.all([this.getFiltersHydrated(), this.preloadBanner()]).then(
        ([filters]) => {
          this.setState({ loaded: true, filters });
        },
      );
    }

    if (this.props.collectionCount !== prevProps.collectionCount) {
      this.setState({
        collectionEmpty: !this.props.collectionCount,
      });
    }

    if (this.props.filters !== prevProps.filters) {
      this.getFiltersHydrated().then((filters) => {
        this.setState({ filters });
      });
    }
  }

  // A banner that loads its own data is shown together with the list, so it
  // never pushes the list down when it arrives later
  preloadBanner() {
    const { banner, client, user } = this.props;

    return banner?.type?.preload?.(banner.props, { client, user });
  }

  async fetchCollection() {
    const { fetchCollection } = this.props;
    const locationQuery = await this.locationQueryWithSearchHandler();

    return fetchCollection(
      locationQuery,
      this.props.tabs,
      this.state.filters,
      this.props.query,
    );
  }

  async locationQueryWithSearchHandler() {
    const { location, locationQuery, searchHandler } = this.props;

    const query = locationQuery || location.query;

    if (searchHandler && query.search) {
      const searchQuery = await searchHandler(query.search, query.limit || 50);

      return {
        ...query,
        where: searchQuery,
        search: undefined,
      };
    } else {
      return query;
    }
  }

  getFiltersHydrated() {
    // Execute in a microtask so that the location object has time to be modified in App.js
    return Promise.resolve(this.props).then(() => this.hydrateFilters());
  }

  async hydrateFilters() {
    const {
      location,
      locationQuery,
      categories,
      filters: filtersProp,
    } = this.props;
    const query = locationQuery || location.query;

    if (!filtersProp) {
      return;
    }

    const filters = objectToArray(filtersProp);

    for (const [key, value] of Object.entries(query)) {
      const filter = find(filters, { id: key });

      if (filter) {
        switch (filter.type) {
          case 'LookupCategory': {
            const category = categories.index.get(value);

            filter.valueLabel = `'${category ? category.name : value}'`;

            break;
          }

          case 'LookupCustomer': {
            const account = await this.fetchCustomer(value);

            filter.valueLabel = `'${account ? accountName(account) : value}'`;

            break;
          }

          case 'LookupProduct': {
            const product = await this.fetchProduct(value);

            filter.valueLabel = `'${product ? product.name : value}'`;

            break;
          }

          case 'date': {
            filter.valueLabels = {
              after: formatDate(value.after, 'short'),
              before: formatDate(value.before, 'short'),
            };

            break;
          }

          default:
            break;
        }
      }
    }

    return filters;
  }

  async fetchCustomer(id) {
    const { setLoading } = this.props;
    setLoading(true);
    const account = await api.get('/data/accounts/{id}', { id });
    setLoading(false);
    return account;
  }

  async fetchProduct(id) {
    const { setLoading } = this.props;
    setLoading(true);
    const product = await api.get('/data/products/{id}', { id });
    setLoading(false);
    return product;
  }

  updateLocationQuery(update, replace = false) {
    const { router, onQuery, location, locationQuery = {} } = this.props;

    if (onQuery) {
      const nextQuery = updateQueryObject(locationQuery, update);
      onQuery(nextQuery);
    } else {
      const nextUri = locationWithQuery(location, update);
      replace ? router.replace(nextUri) : router.push(nextUri);
    }
  }

  onSubmitSearch({ search }) {
    this.updateLocationQuery({ search, page: 1 });
    if (
      this.state.filterListVisible &&
      (this.state.filterValue || this.state.filterValues)
    ) {
      this.applyFilter();
    }
  }

  onClickShowFilterList(event) {
    event.preventDefault();

    this.setState((state) => ({
      filterListVisible: !state.filterListVisible,
    }));
  }

  onChangeSearch({ filterKeys, filterOp, filterValue, filterValues }) {
    this.setState({
      filterKeys: { ...filterKeys },
      filterOp,
      filterValue,
      filterValues,
    });
  }

  onClickSort(field, event) {
    event.preventDefault();
    const { dir, sorted } = event.currentTarget.dataset;

    this.updateLocationQuery(
      { sort: buildSortValue(field, dir, sorted) },
      true,
    );
  }

  applyFilter() {
    const { location, locationQuery } = this.props;
    const { filterKeys, filterValue, filterValues } = this.state;

    const nextProps = reduce(
      filterKeys,
      (acc, active, key) => {
        if (active) {
          if (filterValues && !isEmpty(filterValues[key])) {
            acc[key] = filterValues[key];
          } else if (filterValue && filterValue[key] !== undefined) {
            acc[key] =
              filterValue[key] && filterValue[key].id
                ? filterValue[key].id
                : filterValue[key];
          }
        }

        return acc;
      },
      {},
    );

    const query = pick(locationQuery || location.query, [
      'search',
      'page',
      'limit',
      'tab',
    ]);

    if (query.page === '1') {
      delete query.page;
    }

    if (query.limit === '50') {
      delete query.limit;
    }

    this.updateLocationQuery({ ...nextProps, ...query });
  }

  onClickApplyFilter(event) {
    event.preventDefault();
    this.applyFilter();
  }

  onClickRemoveFilter(event) {
    event.preventDefault();
    const filterKey = event.currentTarget.dataset.key;
    this.updateLocationQuery({ [filterKey]: undefined });
    this.setState((state) => ({
      filterKeys: { ...state.filterKeys, [filterKey]: false },
    }));
  }

  onClickSelectAll(event) {
    event.preventDefault();
    this.props.selectAll();
  }

  onClickSelectRow(event) {
    event.preventDefault();
    this.props.selectOne(event.currentTarget.dataset.id, event.shiftKey);
    if (window.getSelection) {
      // All browsers, except IE <=8
      window.getSelection().removeAllRanges();
    } else if (document.selection) {
      // IE <=8
      document.selection.empty();
    }
  }

  onClickSelectClear(event) {
    event.preventDefault();
    this.props.selectClear();
  }

  onClickPage(event) {
    event.preventDefault();
    this.updateLocationQuery({ page: event.currentTarget.dataset.page });
  }

  onChangeLimit(_event, value) {
    if (!this.state.firstChanged) {
      this.setState({ firstChanged: true });
    } else {
      try {
        window.localStorage.setItem(`${this.props.model}_per_page`, value);
      } catch (err) {
        console.error(err);
      }

      this.updateLocationQuery({
        limit: value,
        page: 1,
      });
    }
  }

  // Refetch the count and the current page, after a bulk or app action
  refreshCollection = async () => {
    const { fetchCollectionCount, fetchCollection } = this.props;

    await Promise.all([
      fetchCollectionCount(this.props.query),
      this.locationQueryWithSearchHandler().then((locationQuery) =>
        fetchCollection(
          locationQuery,
          this.props.tabs,
          this.state.filters,
          this.props.query,
        ),
      ),
    ]);
  };

  onClickBulkAction(event) {
    event.preventDefault();
    const { bulkActions } = this.props;
    const index = event.currentTarget.dataset.action;
    const action = bulkActions[index];

    if (action.modal) {
      const params = {
        ...action.params,
        onComplete: async () => {
          await this.refreshCollection();

          return action.params?.onComplete?.();
        },
      };

      this.context.openModal(action.modal, params);
    } else if (action.onClick) {
      action.onClick(event);
    }
  }

  render() {
    const { component, title, headerTitle, tabs, view } = this.props;
    const { loaded, collectionEmpty } = this.state;
    const { client } = this.context;

    if (!loaded) {
      return <CollectionLoading />;
    }

    if (component) {
      return <component {...this.props} {...this.state} />;
    }

    return (
      <div className="collection">
        <View
          {...this.props}
          {...this.state}
          headerTitle={headerTitle !== undefined ? headerTitle : title}
          tabs={collectionEmpty ? null : tabs}
          app={view?.app}
          withPreferences={true}
          onAppActionComplete={this.refreshCollection}
        >
          <CollectionPage {...this.props} {...this.state} apps={client?.apps} />
        </View>
      </div>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withViewPreferences({ id: 'list', type: 'list' }, Collection));
